import { AnalyzeGroupedPosition, GroupedPosition } from '../../../reducer/positions.reducer'
import { Snapshot } from '../../../reducer/security.reducer'
import { TradeOrder } from '../../../reducer/strategyBuilder.reducer'
import { getSecurityKey } from '../../../util/security'
import { Security } from '../../../proto/generated/security'
import { SlTargetType } from './slAndTargetInputs'

export type SlTargetLiveState = {
    isSlValid: boolean
    isTargetValid: boolean
    slInfoText: string | null
    targetInfoText: string | null
    slHelperText: string | null
    targetHelperText: string | null
}

export type ComputeSlTargetLiveParams = {
    effectivePosition: AnalyzeGroupedPosition | GroupedPosition | undefined
    snapshots: Record<string, Snapshot>
    tradeOrders: TradeOrder[]
    selectedSecurity: Security | undefined
    mode: string
    incomingPositions: AnalyzeGroupedPosition[]
    isSingleSecurity: boolean
    isLongPosition: boolean
    singleSecurityQuantity: number
    positionKey: string
    addStopLoss: boolean
    addTarget: boolean
    slValue: number | string
    targetValue: number | string
    slValueType: SlTargetType
    targetValueType: SlTargetType
    showSLTargetInfo: boolean
    effectivePositionAvgPrice?: number
}

function computeNetProfit(
    effectivePosition: AnalyzeGroupedPosition | GroupedPosition,
    snapshots: Record<string, Snapshot>,
    tradeOrders: TradeOrder[],
    selectedSecurity: Security | undefined,
    mode: string,
    incomingPositions: AnalyzeGroupedPosition[]
): number {
    const analyzePosition = effectivePosition as AnalyzeGroupedPosition
    let totalNetProfit = (analyzePosition.children || []).reduce((sum, childPosition) => {
        let pnl = childPosition.realizedProfit || 0
        const originalPosition = childPosition.originalPosition
        if (!originalPosition) return sum + pnl
        const direction = originalPosition.closePosition > 0 ? 1 : -1
        const originalAvgPrice = originalPosition.closePositionValue / originalPosition.closePosition
        const totalQty = Math.abs(originalPosition.closePosition)
        const squaredQty = Math.abs(childPosition.squaredOffQuantity || 0)
        const securityKey = getSecurityKey(childPosition.security.exchange, childPosition.security.code)
        const snapshot = snapshots[securityKey]
        if (!snapshot) return sum + pnl
        const unrealizedQty = totalQty - squaredQty
        const valuationPrice = snapshot.lastTradedPrice
        if (childPosition.squareOffOrderId && squaredQty > 0) {
            const squareOffOrder = tradeOrders?.find((t) => t.clientOrderId === childPosition.squareOffOrderId)
            if (squareOffOrder?.selected) {
                const squareOffPrice = squareOffOrder.price || snapshot.lastTradedPrice
                pnl += squaredQty * (squareOffPrice - originalAvgPrice) * direction
            }
        }
        if (unrealizedQty > 0) {
            pnl += unrealizedQty * (valuationPrice - originalAvgPrice) * direction
        }
        return sum + pnl
    }, 0)

    if (mode === 'analyze' || mode === 'squareOff') {
        const incomingPNL = incomingPositions.reduce((sum, position) => {
            const addedQty = position.addedQuantity ?? 0
            if (addedQty === 0) return sum
            const securityKey = getSecurityKey(position.security.exchange, position.security.code)
            const ltp = snapshots[securityKey]?.lastTradedPrice
            if (!Number.isFinite(ltp)) return sum
            const orderPrice = ltp
            const direction = Math.sign(addedQty)
            const pnl = Math.abs(addedQty) * (ltp - orderPrice) * direction
            return sum + pnl
        }, 0)
        return (totalNetProfit + incomingPNL) / (selectedSecurity?.priceMultiplier ?? 100)
    }

    return totalNetProfit / (selectedSecurity?.priceMultiplier ?? 100)
}

export function computeSlTargetLiveState(params: ComputeSlTargetLiveParams): SlTargetLiveState {
    const {
        effectivePosition,
        snapshots,
        tradeOrders,
        selectedSecurity,
        mode,
        incomingPositions,
        isSingleSecurity,
        isLongPosition,
        singleSecurityQuantity,
        positionKey,
        addStopLoss,
        addTarget,
        slValue,
        targetValue,
        slValueType,
        targetValueType,
        showSLTargetInfo,
        effectivePositionAvgPrice,
    } = params

    const netProfit =
        effectivePosition != null
            ? computeNetProfit(
                  effectivePosition as AnalyzeGroupedPosition,
                  snapshots,
                  tradeOrders,
                  selectedSecurity,
                  mode,
                  incomingPositions
              )
            : 0

    const currentLtp =
        isSingleSecurity && positionKey
            ? (() => {
                  const raw = snapshots[positionKey]?.lastTradedPrice
                  if (raw == null) return null
                  return raw / (selectedSecurity?.priceMultiplier ?? 100)
              })()
            : null

    const calculatedSlValue = (() => {
        if (!addStopLoss || !slValue) return null
        if (isSingleSecurity) {
            if (!effectivePositionAvgPrice) return null
            const entryPrice = effectivePositionAvgPrice / (selectedSecurity?.priceMultiplier ?? 100)
            if (slValueType === 'exact') {
                return isLongPosition ? entryPrice - Number(slValue) : Number(slValue) - entryPrice
            }
            if (slValueType === 'points') {
                return Number(slValue) * singleSecurityQuantity
            }
            const percentageChange = (entryPrice * Number(slValue)) / 100
            return percentageChange
        }
        return Number(slValue)
    })()

    const calculatedTargetValue = (() => {
        if (!addTarget || !targetValue) return null
        if (isSingleSecurity) {
            if (!effectivePositionAvgPrice) return null
            const entryPrice = effectivePositionAvgPrice / (selectedSecurity?.priceMultiplier ?? 100)
            if (targetValueType === 'exact') {
                return isLongPosition ? Number(targetValue) - entryPrice : entryPrice - Number(targetValue)
            }
            if (targetValueType === 'points') {
                return Number(targetValue) * singleSecurityQuantity
            }
            const percentageChange = (entryPrice * Number(targetValue)) / 100
            return percentageChange
        }
        return Number(targetValue)
    })()

    const calculatedTargetLtp = (() => {
        if (!isSingleSecurity || !addTarget || !targetValue || !effectivePositionAvgPrice) return null
        const entryPrice = effectivePositionAvgPrice / (selectedSecurity?.priceMultiplier ?? 100)
        if (targetValueType === 'exact') return Number(targetValue)
        if (targetValueType === 'points') {
            return isLongPosition ? entryPrice + Number(targetValue) : entryPrice - Number(targetValue)
        }
        const percentageChange = (entryPrice * Number(targetValue)) / 100
        return isLongPosition ? entryPrice + percentageChange : entryPrice - percentageChange
    })()

    const calculatedSlLtp = (() => {
        if (!isSingleSecurity || !addStopLoss || !slValue || !effectivePositionAvgPrice) return null
        const entryPrice = effectivePositionAvgPrice / (selectedSecurity?.priceMultiplier ?? 100)
        if (slValueType === 'exact') return Number(slValue)
        if (slValueType === 'points') {
            return isLongPosition ? entryPrice - Number(slValue) : entryPrice + Number(slValue)
        }
        const percentageChange = (entryPrice * Number(slValue)) / 100
        return isLongPosition ? entryPrice - percentageChange : entryPrice + percentageChange
    })()

    const isTargetValid = (() => {
        if (!addTarget || !targetValue) return true
        if (isSingleSecurity && currentLtp != null && calculatedTargetLtp != null) {
            return isLongPosition ? calculatedTargetLtp > currentLtp : calculatedTargetLtp < currentLtp
        }
        if (netProfit === undefined || netProfit === null) return true
        if (netProfit === 0) {
            return (calculatedTargetValue ?? 0) > 0
        }
        return (calculatedTargetValue ?? 0) >= netProfit
    })()

    const isSlValid = (() => {
        if (!addStopLoss || !slValue) return true
        if (isSingleSecurity && currentLtp != null && calculatedSlLtp != null) {
            return isLongPosition ? calculatedSlLtp < currentLtp : calculatedSlLtp > currentLtp
        }
        if (netProfit === undefined || netProfit === null) return true
        if (netProfit === 0) {
            return (calculatedSlValue ?? 0) > 0
        }
        return (calculatedSlValue ?? 0) >= -netProfit
    })()

    const targetHelperText = (() => {
        if (!isSingleSecurity || !showSLTargetInfo || !targetValue || targetValueType === 'exact' || !isTargetValid) {
            return null
        }
        const entryPrice = (effectivePositionAvgPrice ?? 0) / (selectedSecurity?.priceMultiplier ?? 100)
        const targetPrice = isLongPosition
            ? entryPrice + (calculatedTargetValue ?? 0) / singleSecurityQuantity
            : entryPrice - (calculatedTargetValue ?? 0) / singleSecurityQuantity
        return `Target LTP : ${targetPrice.toFixed(2)}`
    })()

    const slHelperText = (() => {
        if (!isSingleSecurity || !showSLTargetInfo || !slValue || slValueType === 'exact' || !isSlValid) {
            return null
        }
        const entryPrice = (effectivePositionAvgPrice ?? 0) / (selectedSecurity?.priceMultiplier ?? 100)
        const slPrice = isLongPosition
            ? entryPrice - (calculatedSlValue ?? 0) / singleSecurityQuantity
            : entryPrice + (calculatedSlValue ?? 0) / singleSecurityQuantity
        return `SL LTP : ${slPrice.toFixed(2)}`
    })()

    const targetInfoText = (() => {
        if (isSingleSecurity) {
            if (currentLtp == null) return null
            return isLongPosition
                ? `Target must be > ${currentLtp.toFixed(2)}`
                : `Target must be < ${currentLtp.toFixed(2)}`
        }
        return `Target must be >= ${netProfit.toFixed(2)}`
    })()

    const slInfoText = (() => {
        if (isSingleSecurity) {
            if (currentLtp == null) return null
            return isLongPosition
                ? `SL LTP must be < ${currentLtp.toFixed(2)}`
                : `SL LTP must be > ${currentLtp.toFixed(2)}`
        }
        return `SL must be >= ${(-netProfit).toFixed(2)}`
    })()

    return {
        isSlValid,
        isTargetValid,
        slInfoText,
        targetInfoText,
        slHelperText,
        targetHelperText,
    }
}

export function slTargetLiveStateEquals(a: SlTargetLiveState, b: SlTargetLiveState): boolean {
    return (
        a.isSlValid === b.isSlValid &&
        a.isTargetValid === b.isTargetValid &&
        a.slInfoText === b.slInfoText &&
        a.targetInfoText === b.targetInfoText &&
        a.slHelperText === b.slHelperText &&
        a.targetHelperText === b.targetHelperText
    )
}
